import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Stack,
  Button,
  MenuItem,
  Typography,
  Divider,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import {
  FormProvider,
  Iconify,
  RHFDatePicker,
  RHFSelect,
  RHFTextField,
  RHFUploadAvatar,
} from '@app/components';
import { Strings } from '@app/constants';
import useResponsive from '@app/hooks/use-responsive';
import { IMyProfileForm, IMyProfileResponse } from '../types';
import { StyledInputs } from './style';
import { updateProfile } from './slice';
import { useRequestState } from '@app/hooks';
import { selectUpdateMyProfile } from './selectors';
import { relation } from '@app/mock/pesonal-info';
import { generalInformationSchema } from '../validators';

interface Props {
  row?: IMyProfileResponse;
}

const GeneralInformationForm = ({ row }: Props) => {
  const [disabled, setDisabled] = useState(row?.samePresentAddress);

  const initialInformation = {
    name: '',
    relation: '',
    phone: '',
  };
  const dispatch = useDispatch();
  const methods = useForm<IMyProfileForm>({
    resolver: yupResolver(generalInformationSchema) as any,
    defaultValues: {
      firstName: get(row, 'firstName', ''),
      lastName: get(row, 'lastName', ''),
      dob: get(row, 'dob', ''),
      mobileNumber: get(row, 'phone', ''),
      personalEmail: get(row, 'personalEmail', ''),
      gender: get(row, 'gender', ''),
      maritalStatus: get(row, 'maritalStatus', ''),
      aboutMe: get(row, 'aboutMe', ''),
      myProfileImage: get(row, 'profileImage', ''),
      presentAddress: row?.presentAddress ? row?.presentAddress : {},
      permanentAddress: row?.permanentAddress ? row?.permanentAddress : {},
      emergencyContactInfo: {
        emergencyContact: row?.emergencyContact ? [...row?.emergencyContact] : [],
      },
    },
  });

  const emergencyContact = row?.emergencyContact;
  const [information, setInformation] = useState(
    emergencyContact ? emergencyContact ?? [] : [initialInformation]
  );
  useRequestState({
    stateSelector: selectUpdateMyProfile,
    successMessageShown: true,
    errorShown: true,
  });

  const handleRemoveClick = (index: number) => {
    const updateInformation = information.filter((_, i) => i !== index);
    setInformation(updateInformation);
  };

  const handleCheckboxChange = (e: any) => {
    const isChecked = e.target.checked;
    const presentAddress = methods.getValues('presentAddress');

    if (isChecked) {
      methods.setValue('samePresentAddress', e.target.checked);

      methods.setValue('permanentAddress.address', presentAddress.address);
      methods.setValue('permanentAddress.addressLine2', presentAddress.addressLine2);
      methods.setValue('permanentAddress.city', presentAddress.city);
      methods.setValue('permanentAddress.state', presentAddress.state);
      methods.setValue('permanentAddress.country', presentAddress.country);
      methods.setValue('permanentAddress.postalCode', presentAddress.postalCode);
      setDisabled(true);
    } else {
      methods.setValue('samePresentAddress', e.target.checked);

      methods.setValue('permanentAddress.address', '');
      methods.setValue('permanentAddress.addressLine2', '');
      methods.setValue('permanentAddress.city', '');
      methods.setValue('permanentAddress.state', '');
      methods.setValue('permanentAddress.country', '');
      methods.setValue('permanentAddress.postalCode', '');
      setDisabled(false);
    }
  };

  const renderInputFieldSets = () => {
    return information.map((information, index) => (
      <>
        {index === 0 ? null : (
          <Stack alignItems={'flex-end'}>
            <IconButton
              onClick={() => {
                handleRemoveClick(index);
              }}
              sx={{
                width: '3vw',
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Iconify icon="material-symbols:close" />
            </IconButton>
          </Stack>
        )}
        <Stack flexWrap="wrap">
          <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].name`}
              label={`${Strings.addEmployee.name}`}
            />
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].relation`}
              label={`${Strings.addEmployee.relation}`}
              select
            >
              {relation.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </RHFTextField>
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].phone`}
              label={`${Strings.addEmployee.phone}`}
            />
          </Stack>
        </Stack>
      </>
    ));
  };

  const isMobile = useResponsive('up', 'md');
  const direction = isMobile ? 'row' : 'column';

  const onSubmit = (form: IMyProfileForm) => {
    dispatch(updateProfile({ ...form, id: row?.id }));
  };

  const handleAddMoreClick = () => {
    setInformation((prevInformation: any) => [...prevInformation, { ...initialInformation }]);
  };

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('myProfileImage', newFile);
      }
    },
    [methods]
  );

  return (
    <Stack>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <StyledInputs>
          <Stack spacing={2}>
            <RHFUploadAvatar onDrop={handleDrop} name={'myProfileImage'} />
          </Stack>
          <Stack spacing={2} m={2} width="100%">
            <Stack direction={isMobile ? 'row' : 'column'} spacing={2}>
              <RHFTextField name="firstName" label={`${Strings.field.firstName}*`} />
              <RHFTextField name="lastName" label={`${Strings.field.lastName}*`} />
              <RHFDatePicker
                name="dob"
                label={Strings.addEmployee.dateOfBirth}
                defaultValue={row?.dob ? dayjs(row?.dob, 'DD/MM/YYYY') : null}
                sx={{ width: '100%' }}
              />
            </Stack>
            <Stack direction={isMobile ? 'row' : 'column'} spacing={2}>
              <RHFTextField name="mobileNumber" label={`${Strings.field.mobileNumber}*`} />
              <RHFTextField name="personalEmail" label={`${Strings.field.personalEmail}*`} />
            </Stack>
            <Stack direction={isMobile ? 'row' : 'column'} spacing={2}>
              <RHFSelect name="gender" label={Strings.field.gender}>
                <MenuItem value={Strings.addEmployee.male}>{Strings.addEmployee.male}</MenuItem>
                <MenuItem value={Strings.addEmployee.female}>{Strings.addEmployee.female}</MenuItem>
                <MenuItem value={Strings.addEmployee.other}>{Strings.addEmployee.other}</MenuItem>
              </RHFSelect>
              <RHFSelect name="maritalStatus" label={Strings.addEmployee.maritalStatus}>
                <MenuItem value={Strings.addEmployee.married}>
                  {Strings.addEmployee.married}
                </MenuItem>
                <MenuItem value={Strings.addEmployee.unmarried}>
                  {Strings.addEmployee.unmarried}
                </MenuItem>
              </RHFSelect>
            </Stack>
            <Stack direction={direction} spacing={2}>
              <RHFTextField
                name="aboutMe"
                label={Strings.addEmployee.aboutMe}
                multiline
                minRows={3}
              />
            </Stack>
            <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
              <Stack gap={2} flex={1}>
                <Stack fontWeight={600}> {Strings.addEmployee.presentAddress}</Stack>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="presentAddress.address"
                    label={`${Strings.addEmployee.address} Line 1`}
                    multiline
                  />
                  <RHFTextField
                    name="presentAddress.addressLine2"
                    label={`${Strings.addEmployee.address} Line 2`}
                    multiline
                  />
                </Stack>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="presentAddress.city"
                    label={`${Strings.addEmployee.city}`}
                    multiline
                  />
                  <RHFTextField
                    name="presentAddress.state"
                    label={`${Strings.addEmployee.state}`}
                    multiline
                  />
                </Stack>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="presentAddress.country"
                    label={`${Strings.addEmployee.country}`}
                    multiline
                  />
                  <RHFTextField
                    name="presentAddress.postalCode"
                    label={`${Strings.addEmployee.postalCode}`}
                    multiline
                  />
                </Stack>
              </Stack>
              <Stack flex={1}>
                <Stack
                  display="flex"
                  flexDirection="row"
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography fontWeight={600} textAlign="center">
                    {Strings.addEmployee.permanentAddress}
                  </Typography>
                  <Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="samePresentAddress"
                          checked={disabled}
                          onChange={handleCheckboxChange}
                          inputProps={{ 'aria-label': 'Copy Address' }}
                        />
                      }
                      label={Strings.addEmployee.sameAsPresentAddress}
                    />
                  </Stack>
                </Stack>
                <Stack gap={2}>
                  <Stack direction={direction} spacing={2}>
                    <RHFTextField
                      name="permanentAddress.address"
                      label={`${Strings.addEmployee.address} Line 1`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.address'),
                      }}
                    />
                    <RHFTextField
                      name="permanentAddress.addressLine2"
                      label={`${Strings.addEmployee.address} Line 2`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.addressLine2'),
                      }}
                    />
                  </Stack>
                  <Stack direction={direction} spacing={2}>
                    <RHFTextField
                      name="permanentAddress.city"
                      label={`${Strings.addEmployee.city}`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.city'),
                      }}
                    />
                    <RHFTextField
                      name="permanentAddress.state"
                      label={`${Strings.addEmployee.state}`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.state'),
                      }}
                    />
                  </Stack>
                  <Stack direction={direction} spacing={2}>
                    <RHFTextField
                      name="permanentAddress.country"
                      label={`${Strings.addEmployee.country}`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.country'),
                      }}
                    />
                    <RHFTextField
                      name="permanentAddress.postalCode"
                      label={`${Strings.addEmployee.postalCode}`}
                      multiline
                      disabled={disabled}
                      InputLabelProps={{
                        shrink: !!methods.getValues('permanentAddress.postalCode'),
                      }}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <Stack my={2} gap={1}>
                <Typography variant="h6">{Strings.addEmployee.emergency}</Typography>
              </Stack>
              <Stack mb={3}>
                <Divider />
              </Stack>
              {renderInputFieldSets()}
              <Stack flexDirection={'row'} justifyContent={'flex-end'} pt={2}>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  sx={{ width: 'max-content' }}
                  onClick={handleAddMoreClick}
                >
                  {Strings.button.addMore}
                  <IconButton>
                    <Iconify icon="ic:outline-plus" />
                  </IconButton>
                </Button>
              </Stack>
            </Stack>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              sx={{ width: 'max-content' }}
              type="submit"
            >
              {Strings.button.saveGeneralInformation}
            </Button>
          </Stack>
        </StyledInputs>
      </FormProvider>
    </Stack>
  );
};
export { GeneralInformationForm };
